import React, { useState } from 'react';
import clsx from 'clsx';
import { NumberField } from '@/components/inspector-fields/NumberField/NumberField';

import IconLink from '@/assets/icons/icon-link.svg?react';
import IconLinked from '@/assets/icons/icon-linked.svg?react';

export type DimensionDragOptions = { isWidth: boolean; isHeight: boolean };
const widthOptions: DimensionDragOptions = { isWidth: true, isHeight: false };
const heightOptions: DimensionDragOptions = { isWidth: false, isHeight: true };

interface DimensionsFieldsProps {
  aspectRatioLock: boolean;
  className: string;
  disableWidth?: boolean;
  disableHeight?: boolean;
  width: number;
  height: number;
  hasAspectControl: boolean;
  onChange?: (value: number, options: DimensionDragOptions) => void;
  onChangeComplete?: (value: number, initialValue: number, options: DimensionDragOptions) => void;
  onDragStart?: (value: number, options: DimensionDragOptions) => void;
  onDrag?: (value: number, options: DimensionDragOptions) => void;
  onDragEnd?: (value: number, options: DimensionDragOptions) => void;
  onChangeAspectRatioLock: () => void;
}

export const DimensionsFields: React.FC<DimensionsFieldsProps> = ({
  className = '',
  disableWidth,
  disableHeight,
  width = 1024,
  height = 768,
  hasAspectControl,
  aspectRatioLock,
  onChange,
  onChangeAspectRatioLock,
  onChangeComplete,
  onDragStart,
  onDrag,
  onDragEnd,
}) => {
  const [initialStateHeight, setInitialStateHeight] = useState(height);
  const [initialStateWidth, setInitialStateWidth] = useState(width);

  const handleHeightChangeComplete = (
    event: React.KeyboardEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement, Element>,
  ) => {
    const value = Number(event.currentTarget.value);
    const initialValue = initialStateHeight || 0;
    onChangeComplete?.(value, initialValue, heightOptions);
    setInitialStateHeight(value);
  };

  const handleWidthChangeComplete = (
    event: React.KeyboardEvent<HTMLInputElement> | React.FocusEvent<HTMLInputElement, Element>,
  ) => {
    const value = Number(event.currentTarget.value);
    const initialValue = initialStateWidth || 0;
    onChangeComplete?.(value, initialValue, widthOptions);
    setInitialStateWidth(value);
  };

  const handleToggleLock = () => {
    // Ratio calculation is now handled in parent (NodeDimensionSettings)
    onChangeAspectRatioLock();
  };

  return (
    <div className={clsx('flex items-center gap-2', className)}>
      <div className="flex w-full items-center justify-between gap-2">
        <div
          className={clsx(
            'flex items-center gap-2',
            disableWidth && 'pointer-events-none opacity-50',
          )}
        >
          <p className="font-['Inter'] text-xs leading-normal font-medium text-neutral-500">W</p>
          <NumberField
            className=""
            classNameInput="min-w-[68px]"
            max={5120}
            min={1}
            onChange={onChange ? (v) => onChange(v, widthOptions) : () => {}}
            onChangeComplete={handleWidthChangeComplete}
            onDragStart={onDragStart ? (v) => onDragStart(v, widthOptions) : undefined}
            onDrag={onDrag ? (v) => onDrag(v, widthOptions) : undefined}
            onDragEnd={onDragEnd ? (v) => onDragEnd(v, widthOptions) : undefined}
            step={1}
            value={width ?? 0}
            disabled={disableWidth}
          />
        </div>
        <div
          className={clsx(
            'flex items-center gap-2',
            disableHeight && 'pointer-events-none opacity-50',
          )}
        >
          <p className="font-['Inter'] text-xs leading-none font-medium text-neutral-500">H</p>
          <NumberField
            className=""
            classNameInput="min-w-[68px]"
            max={5120}
            min={1}
            onChange={onChange ? (v) => onChange(v, heightOptions) : () => {}}
            onChangeComplete={handleHeightChangeComplete}
            onDragStart={onDragStart ? (v) => onDragStart(v, heightOptions) : undefined}
            onDrag={onDrag ? (v) => onDrag(v, heightOptions) : undefined}
            onDragEnd={onDragEnd ? (v) => onDragEnd(v, heightOptions) : undefined}
            step={1}
            value={height ?? 0}
            disabled={disableHeight}
          />
        </div>
      </div>

      {hasAspectControl && (
        <div
          className="ml-auto flex items-center justify-center"
          data-testid="aspect-ratio-lock-container"
        >
          {aspectRatioLock ? (
            <IconLinked
              className="h-4.5 w-4.5 cursor-pointer text-indigo-500"
              onClick={handleToggleLock}
            />
          ) : (
            <IconLink
              className="h-4.5 w-4.5 cursor-pointer text-indigo-500"
              onClick={handleToggleLock}
            />
          )}
        </div>
      )}
    </div>
  );
};
